/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useIsMobile from '../../../../shared/hooks/useIsMobile';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
} from '../../ducks/calendarActions';
import SwapPropDropdownCircuit from './SwapPropDropdownCircuit';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import TBportalTooltip from '../../../../shared/components/TBportalTooltip';
import Text from '../../../../shared/components/Text/Text';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import { useEnterSavesForm } from './hooks/useEnterSavesForm';

const FormWrapper = styled('form')`
  display: flex;
  width: 100%;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;

const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: ${(props) => (props.isAdding ? '16px' : '0px')};
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  max-height: ${(props) => (props.isAdding && '24px')};
  margin-bottom: 3px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? `1px solid ${props.theme.colors.borderLight}` : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : props.theme.colors.text)};
  }
`;

const SetRowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: center;
  border-radius: 4px;
`;

const SaveButtonContainer = styled('button')`
  display: flex;
  background: #10cd8c;
  height: 34px;
  padding-left: 8px;
  padding-right: 8px;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.shimmer ? 'transparent' : props.theme.colors.white)};
  border-radius: 5px;
  font-weight: 700;
  font-size: 13px;
  align-items: center;
  min-width: 56px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  width: 100%;
`;

const RemoveIcon = styled('div')`
  padding: 4px 4px 4px 8px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RowsContainer = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-end;
  height: 40px;
  gap: 16px;
  margin-bottom: ${(props) => (props.hasError ? '12px' : '0px')};
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const InputWithErrorWrapper = styled('div')`
  position: relative;
  width: 100%;
`;

const InputError = styled('div')`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -2px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: red;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: default;
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const SetRowValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const ShimmerSetRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  margin-right: 5px;
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const PrimaryValueContainer = styled('div')`
  flex: 1;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const SetPrimaryValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const TooltipDataContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 16px;
  padding-left: 16px;
  gap: 10px;
  border-radius: 4px;
  max-width: 250px;
`;

const ErrorAndRowsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SaveButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  margin-bottom: 16px;
  padding-top: 18px;
  margin-top: 32px;
`;

const SaveButtonContainerText = styled('div')`
  margin-top: 2px;
  font-weight: 700;
  font-family: 'Nunito Sans';
`;

const PreSaveTableCircuit = ({
  coachCompletion,
  completionOnly,
  contentIndex,
  content,
  localAdditionalInfo,
  localDescription,
  swapDropdownSelectedExercise,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  /** Enter anywhere in this table saves it, exactly as clicking SAVE does —
   * blurring the focused cell first so its onBlur normalization runs. Stands
   * down inside month view's popover, which owns Enter there. */
  useEnterSavesForm(tableContainerRef);

  const targetRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [errors, setErrors] = useState({});
  const errorTooltipId = `circuit-presave-error-tooltip-${content.assignedId}`;
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(effectiveDate).format('YYYY-MM-DD');
  const calendarContainerScrollPositionTop = useSelector((state) => state.calendar.data.calendarContainerScrollPositionTop);
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const isMobile = useIsMobile();
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );
  const [setNumberValue, setSetNumberValue] = useState(content?.preSaveRounds || 1);
  const [exerciseData, setExerciseData] = useState({ reps: content?.detailType === 'reps' ? content?.detail : null, sets: content?.detailType === 'sets' ? content?.detail : null });
  const [time, setTime] = useState(!content?.detailType ? content?.detail : null);
  const [rest, setRest] = useState(content?.preSaveRest || null);

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        icon: 'circuit',
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
      };
      if (programType === 'free-form-program') {
        dispatch(updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex));
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'rounds' },
    { name: 'rest', label: 'Rest (s)' },
    /** { name: 'time limit' }, */
    /** need to bring in initial values off of conent to determine if reps or sets are shown */
    { name: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'sets') || content.inputType === 'S' ? 'sets' : 'reps' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    rounds: true,
    rest: true,
    /** 'time limit': true, */
    /** need to bring in initial value off of conent */
    reps: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'reps') || content.inputType === 'R',
    /** need to bring in initial value off of conent */
    sets: ((content?.inputType === 'T' || content?.inputType === 'C') && content.detailType === 'sets') || content.inputType === 'S',
    'add-property': true,
  });

  const addPropertyInitialized = useRef(false);

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    // Skip on initial mount to allow backup restoration to complete
    if (!addPropertyInitialized.current) {
      addPropertyInitialized.current = true;
      return;
    }

    // For circuit tables, only rest and one of (reps OR sets) can be enabled at a time
    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
    && (primaryRowSwitch.reps || primaryRowSwitch.sets);

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.reps,
    primaryRowSwitch.sets,
  ]);

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  useEffect(() => {
    if (!primaryRowSwitch.time) {
      setTime(null);
    }
    if (!primaryRowSwitch.rest) {
      setRest(null);
    }
  }, [primaryRowSwitch]);

  // Initialize state from Redux backup if it exists
  // Only runs on mount or when switching to a different item (not on every backup change)
  useEffect(() => {
    const backup = preSaveTableLocalStateForItem;
    if (backup) {
      if (backup.setNumberValue !== undefined) setSetNumberValue(backup.setNumberValue);
      if (backup.exerciseData !== undefined) setExerciseData(backup.exerciseData);
      if (backup.time !== undefined) setTime(backup.time);
      if (backup.rest !== undefined) setRest(backup.rest);
      if (backup.primaryRowSwitch !== undefined) setPrimaryRowSwitch(backup.primaryRowSwitch);
      if (backup.primaryRows !== undefined) setPrimaryRows(backup.primaryRows);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.assignedId]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      exerciseData,
      time,
      rest,
      primaryRowSwitch,
      primaryRows,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, exerciseData, time, rest, primaryRowSwitch, primaryRows,
    content.assignedId, dispatch,
  ]);

  // Track if we've completed initial mount/restore cycle
  const hasInitialized = useRef(false);
  const { colors } = useTheme();

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }
    saveStateToRedux();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setNumberValue,
    exerciseData,
    time,
    rest,
    primaryRowSwitch,
    primaryRows,
  ]);

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const setTimeFunction = (value) => {
    setTime(value);
  };

  const handleInputChange = (field, value) => {
    // Prevent negative values for numeric fields
    if ((field === 'reps' || field === 'sets') && parseFloat(value) < 0) {
      return;
    }
    setExerciseData((prev) => ({ ...prev, [field]: value }));
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  const handleSaveCalendarExercise = (contentIndexParam) => {
    setIsAdding(true);
    const setSubmissionArray = [];
    for (let i = 0; i < setNumberValue; i += 1) {
      const setObject = {
        setNumber: i + 1,
      };
      if (exerciseData.reps) {
        setObject.reps = exerciseData.reps;
      }
      setSubmissionArray.push(setObject);
    }
    const submissionObject = {
      coachCompletion,
      completionOnly,
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: typeFormatter(content.typeDescription),
      sets: setSubmissionArray,
      showSessionBreakItems: false,
    };
    if (rest) {
      submissionObject.restTime = rest;
    }
    if (localDescription || content.description) {
      submissionObject.description = localDescription || content.description;
    }
    if (localAdditionalInfo || content.additionalInfo) {
      submissionObject.additionalInfo = localAdditionalInfo || content.additionalInfo;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(effectiveDate).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        errorAddReset,
        {
          onSuccess: () => {
          // Trigger focus to next preSave item
            dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
          },
          exerciseName: content.title,
        },
        content.assignedId,
      ));
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <InputWithErrorWrapper>
            <RowValue
              isError={!!errors.rest}
              type='number'
              value={rest}
              onChange={(e) => setRestFunction(e.target.value)}
              onBlur={saveStateToRedux}
              onKeyDown={(e) => {
                if (e.key === '-') {
                  e.preventDefault();
                }
              }}
              background={colors.surfaceSecondary}
              data-focus-target={
                (name === 'rest' && primaryRowSwitch.rest && !primaryRowSwitch.rounds)
                  ? content.assignedId
                  : undefined
              }
            />
            {errors.rest && (
              <InputError
                data-tooltip-id={errorTooltipId}
                data-tooltip-content={errors.rest}
              >
                {errors.rest}
              </InputError>
            )}
          </InputWithErrorWrapper>
        </RowValueContainer>
      );
    }
    if (name === 'completed time') {
      return (
        <RowValueContainer>
          <RowValue
            value={time}
            placeholder='-'
            /** disable the ability to edit this field */
            disabled
            onChange={(e) => setTimeFunction(e.target.value)}
            background={colors.surfaceSecondary}
            textColor={colors.textPlaceholder}
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <RowValue
          value={name === 'reps' ? exerciseData?.reps : exerciseData?.sets}
          /** disable ablity to edit these fields */
          disabled
          placeholder='-'
          type='text'
          background={colors.surfaceSecondary}
          textColor={colors.textPlaceholder}
          onChange={(e) => handleInputChange(name, e.target.value)}
          onKeyDown={(e) => {
            // Prevent minus key for numeric fields
            if ((name === 'reps' || name === 'sets') && e.key === '-') {
              e.preventDefault();
            }
          }}
        />
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      return `(${unitContent?.unitAbbr === 'wgt' ? 'lbs' : unitContent?.unitAbbr})`;
    }
    return '';
  };

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (!isMobile && focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [isMobile, focusTarget, content.assignedId]);

  return (
    <FormWrapper
      ref={tableContainerRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveCalendarExercise(contentIndex);
      }}
    >
      <Container>
        <PrimaryRow isAdding={isAdding}>
          {primaryRows.map((primaryRow) => (
            primaryRowSwitch[primaryRow.name] ? (
              primaryRow.name === 'rounds' ? !isAdding ? <SetPrimaryValueContainer>{capitalizeWords(primaryRow.name)}</SetPrimaryValueContainer>
                : (
                  <Shimmer>
                    <SetPrimaryValueContainer shimmer>
                      {capitalizeWords(primaryRow.name)}
                    </SetPrimaryValueContainer>
                  </Shimmer>
                ) : primaryRow.name === 'add-property' ? (
                !isAdding ? (
                  <PrimaryValueContainer>
                    <AddPropertyColumnComponent
                      mode='header'
                      primaryRows={primaryRows}
                      primaryRowSwitch={primaryRowSwitch}
                      setPrimaryRowSwitch={setPrimaryRowSwitch}
                      setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                      containerRef={tableContainerRef}
                      assignedId={content.assignedId}
                    />
                  </PrimaryValueContainer>
                ) : (
                  <Shimmer>
                    <PrimaryValueContainer shimmer>
                      <PrimaryRowValue isAdding={isAdding}>
                        Add Property
                      </PrimaryRowValue>
                    </PrimaryValueContainer>
                  </Shimmer>
                )
              ) : (
                !isAdding ? (
                  <PrimaryValueContainer>
                    <PrimaryRowValue isAdding={isAdding}>
                      {/* the conditions for athlete input only tooltip */}
                      {(primaryRow.name === 'reps' && content.inputType === 'R') || (primaryRow.name === 'sets' && content.inputType === 'S') || (primaryRow.name === 'completed time' && content.inputType === 'T') ? (
                        <NameAndDropdown
                          ref={targetRef}
                        >
                          {((primaryRow.name === 'reps') || (primaryRow.name === 'sets') || (primaryRow.name === 'completed time')) && (
                          <InfoIconWrapper>
                            <IcomoonReact
                              iconSet={iconSet}
                              size={14}
                              icon='info2'
                              color={colors.textSecondary}
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={() => setShowTooltip(true)}
                              onMouseLeave={() => setShowTooltip(false)}
                            />
                          </InfoIconWrapper>
                          )}
                          {showTooltip && (
                          <TBportalTooltip targetRef={targetRef} backgroundColor='rgba(68, 68, 68, 1)' setShowToolTip={setShowTooltip} scrollContainerRefTopPosition={calendarContainerScrollPositionTop}>
                            <TooltipDataContainer>
                              <Text fontSize='13px' fontWeight={400} color={colors.white}>Athlete input only</Text>
                            </TooltipDataContainer>
                          </TBportalTooltip>
                          )}
                          <SwapPropDropdownCircuit
                            visible={false}
                            setPrimaryRowSwitch={setPrimaryRowSwitch}
                            primaryRowSwitch={primaryRowSwitch}
                            rowName={primaryRow.name}
                            size={12}
                            updateColumnName={updateColumnName}
                            name={primaryRow.name === 'time' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.label || primaryRow.name)}
                            unit={primaryRow.name === 'time' ? findCorrectUnit(content) : null}
                          />
                        </NameAndDropdown>
                      ) : (
                        <NameAndDropdown>
                          <SwapPropDropdownCircuit
                        /** coach SHOULD always be able to edit and swap rest and rounds */
                            visible={primaryRow.name === 'rounds' || primaryRow.name === 'rest'}
                            setPrimaryRowSwitch={setPrimaryRowSwitch}
                            primaryRowSwitch={primaryRowSwitch}
                            rowName={primaryRow.name}
                            size={12}
                            updateColumnName={updateColumnName}
                            name={primaryRow.name === 'time' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.label || primaryRow.name)}
                            unit={primaryRow.name === 'time' ? findCorrectUnit(content) : null}
                          />
                        </NameAndDropdown>
                      )}
                    </PrimaryRowValue>
                  </PrimaryValueContainer>
                ) : (
                  <Shimmer>
                    <PrimaryValueContainer shimmer>
                      <PrimaryRowValue isAdding={isAdding}>
                        <NameAndDropdown>
                          {primaryRow.name === 'time' ? capitalizeWords('Prescribed Value') : capitalizeWords(primaryRow.label || primaryRow.name)}
                          {' '}
                          {primaryRow.name === 'time' ? `(${content?.exercise?.unitAbbr})` : null}
                        </NameAndDropdown>
                      </PrimaryRowValue>
                    </PrimaryValueContainer>
                  </Shimmer>
                )
              )
            ) : null
          ))}
        </PrimaryRow>
        <ValueRow>
          <ErrorAndRowsContainer>
            <RowsContainer hasError={Object.values(errors).some(Boolean)}>
              {primaryRows.map((primaryRow) => (
                primaryRowSwitch[primaryRow.name] ? (
                  primaryRow.name === 'rounds' ? (
                    !isAdding ? (
                      <SetRowValueContainer>
                        <SetRowValue
                          type='number'
                          value={setNumberValue}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            // Prevent negative values
                            if (value < 0) return;
                            setSetNumberValue(value);
                          }}
                          onBlur={saveStateToRedux}
                          onKeyDown={(e) => {
                            if (e.key === '-') {
                              e.preventDefault();
                            }
                          }}
                          data-focus-target={primaryRowSwitch.rounds ? content.assignedId : undefined}
                        />
                      </SetRowValueContainer>
                    ) : (
                      <Shimmer>
                        <SetRowValueContainer shimmer>
                          <ShimmerSetRowValue />
                        </SetRowValueContainer>
                      </Shimmer>
                    )
                  ) : primaryRow.name === 'add-property' ? (
                    !isAdding ? (
                      <RowValueContainer>
                        <AddPropertyColumnComponent
                          mode='input'
                          primaryRows={primaryRows}
                          primaryRowSwitch={primaryRowSwitch}
                          setPrimaryRowSwitch={setPrimaryRowSwitch}
                          setPrimaryRowSwitchState={setPrimaryRowSwitchState}
                          containerRef={tableContainerRef}
                          assignedId={content.assignedId}
                        />
                      </RowValueContainer>
                    ) : (
                      <Shimmer>
                        <RowValueContainer shimmer>
                          <ShimmerRowValue />
                        </RowValueContainer>
                      </Shimmer>
                    )
                  )
                    : rowValueDecider(primaryRow.name, isAdding)
                ) : null
              ))}
            </RowsContainer>
          </ErrorAndRowsContainer>
        </ValueRow>
      </Container>
      <SaveButtonWrapper>
        {!isAdding ? (
          <SaveButtonContainer type='submit'>
            <SaveButtonContainerText>
              ADD
            </SaveButtonContainerText>
            <RemoveIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={12}
                icon='down-arrow'
                color={colors.white}
              />
            </RemoveIcon>
          </SaveButtonContainer>
        ) : (
          <Shimmer>
            <SaveButtonContainer shimmer>
              <SaveButtonContainerText>
                ADD
              </SaveButtonContainerText>
              <RemoveIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  size={12}
                  icon='down-arrow'
                  color={colors.white}
                />
              </RemoveIcon>
            </SaveButtonContainer>
          </Shimmer>
        )}
      </SaveButtonWrapper>
      <ReactTooltip
        id={errorTooltipId}
        place='top'
        positionStrategy='fixed'
        delayShow={300}
        style={{
          zIndex: 9999,
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: colors.white,
          fontFamily: 'Nunito Sans',
          fontSize: '12px',
          maxWidth: '260px',
          padding: '6px 10px',
          lineHeight: 1.3,
        }}
      />
    </FormWrapper>
  );
};

PreSaveTableCircuit.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string,
  localDescription: PropTypes.string,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};
PreSaveTableCircuit.defaultProps = {
  localAdditionalInfo: '',
  localDescription: '',
  date: null,
  dayNumber: null,
};

export default PreSaveTableCircuit;
